import {
	AddIcon,
	Button,
	Checkbox,
	CopyIcon,
	CycleIcon,
	DeleteIcon,
	Modal,
	ModuleHeader,
	resolveCycleStatus,
	SearchIcon,
	SpinnerWithBackground,
	Text,
} from '@viapeople/components'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { type Column, GenericTable } from '../../components/GenericTable'
import { useToast } from '../../utils/ToastContext'
import type { CycleDashboardStoreInstance, CycleListItem, SearchQuery } from './stores/cycleDashboardStore'

interface CyclesPageProps {
	store: CycleDashboardStoreInstance
}

export const CyclesPage = ({ store }: CyclesPageProps) => {
	const {
		cycles,
		selectedCycle,
		organization,
		actions,
		isLoading,
		error,
		duplicationOptions,
		searchQuery,
		initialize,
		duplicateSelectedCycle,
		deleteSelectedCycle,
		setSelectedCycle,
		setSearchQuery,
		setDuplicationOptions,
		setError,
	} = store()
	const { organizationId } = useParams<{ organizationId: string }>()
	const navigate = useNavigate()
	const toast = useToast()
	const [isDuplicateModalOpen, setIsDuplicateModalOpen] = useState(false)
	const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false)
	const mountedRef = useRef(false)

	useEffect(() => {
		if (!mountedRef.current) {
			mountedRef.current = true
			if (organizationId) {
				initialize(organizationId)
			}
		}
	}, [organizationId, initialize])

	useEffect(() => {
		if (error) {
			toast.showToast(error, 'error')
			setError()
		}
	}, [error, toast, setError])

	const handleOpenDuplicateModal = (cycle: CycleListItem) => {
		setSelectedCycle(cycle)
		setIsDuplicateModalOpen(true)
	}

	const handleOpenDeleteModal = (cycle: CycleListItem) => {
		setSelectedCycle(cycle)
		setIsDeleteModalOpen(true)
	}

	const handleDuplicate = async () => {
		try {
			const newCycleId = await duplicateSelectedCycle()

			toast.showToast('Cycle duplicated successfully')
			navigate(`/organizations/${organizationId}/cycles/${newCycleId}/general`)
		} catch {
			// Error handling is done in the store
		} finally {
			setIsDuplicateModalOpen(false)
		}
	}

	const handleDelete = async () => {
		try {
			await deleteSelectedCycle()
			toast.showToast('Cycle deleted successfully')
		} catch {
			// Error handling is done in the store
		} finally {
			setIsDeleteModalOpen(false)
		}
	}

	const handleRowClick = (cycle: CycleListItem) => {
		navigate(`/organizations/${organizationId}/cycles/${cycle.id}/general`)
	}

	// Create conditional empty state based on search context
	const getEmptyState = useMemo(() => {
		// If user is searching and no results found
		if (searchQuery.search) {
			return {
				icon: SearchIcon,
				title: 'No cycles match your search',
				description: 'Try adjusting your search terms or clear the search to see all cycles',
				// No action button for search empty state - search bar remains visible
			}
		}

		// Default empty state when no cycles exist and no search active
		return {
			icon: CycleIcon,
			title: 'No cycles created yet',
			description: 'Get started by creating a new cycle',
			actionLabel: 'Create cycle',
			onAction: () => navigate(`/organizations/${organizationId}/cycles/create/general`),
		}
	}, [searchQuery, navigate, organizationId])

	if (isLoading) {
		return (
			<main>
				<SpinnerWithBackground backgroundClass='opacity-100' />
			</main>
		)
	}

	const columns: Column<CycleListItem>[] = [
		{
			field: 'name',
			label: 'Name',
			colSpan: 2,
			sortable: true,
			render: (cycle: CycleListItem) => (
				<div className='flex items-center gap-2'>
					<Text variant='xsmall' weight='regular' className='group-hover:text-primary-600'>
						{cycle.name}
					</Text>
					{cycle.isTemplate && (
						<span className='inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800'>
							Template
						</span>
					)}
				</div>
			),
		},
		{
			field: 'launchDate',
			label: 'Launch Date',
			colSpan: 1,
			sortable: true,
			render: (cycle: CycleListItem) => (
				<Text variant='xsmall' weight='regular' className='group-hover:text-primary-600'>
					{cycle.launchDate ? new Date(cycle.launchDate).toLocaleDateString() : '-'}
				</Text>
			),
		},
		{
			field: 'status',
			label: 'Status',
			colSpan: 1,
			sortable: true,
			render: (cycle: CycleListItem) => (
				<Text variant='xsmall' weight='regular' className='group-hover:text-primary-600'>
					{resolveCycleStatus(cycle.status)}
				</Text>
			),
		},
		{
			field: 'id' as keyof CycleListItem,
			label: 'Actions',
			colSpan: 1,
			sortable: false,
			render: (cycle: CycleListItem) => (
				// biome-ignore lint/a11y/noStaticElementInteractions: presentation container for event bubbling
				<div
					role='presentation'
					className='flex justify-start gap-2'
					onClick={(e) => e.stopPropagation()}
					onKeyDown={(e) => e.stopPropagation()}>
					{cycle.actions.includes('duplicateCycle') && (
						<Button
							variant='secondary'
							size='small'
							data-testid={`cycle-duplicate-btn-${cycle.id}`}
							onClick={(e) => {
								e.stopPropagation()
								handleOpenDuplicateModal(cycle)
							}}
							title='Duplicate cycle'>
							<CopyIcon className='h-4 w-4' />
						</Button>
					)}
					{cycle.actions.includes('deleteCycle') && (
						<Button
							variant='secondary'
							size='small'
							data-testid={`cycle-delete-btn-${cycle.id}`}
							onClick={(e) => {
								e.stopPropagation()
								handleOpenDeleteModal(cycle)
							}}
							title='Delete cycle'>
							<DeleteIcon className='h-4 w-4' />
						</Button>
					)}
				</div>
			),
		},
	]

	return (
		<main className='w-full p-6 flex flex-col gap-4'>
			<ModuleHeader
				breadcrumb={[
					{ label: 'Organizations', to: '/organizations' },
					{ label: organization?.name || '', to: `/organizations/${organizationId}` },
					{ label: 'Cycles' },
				]}
				title='Cycle Overview'
				logo={{
					url: organization?.logo || '',
					name: organization?.name || '',
				}}
			/>

			<GenericTable<CycleListItem, SearchQuery>
				data={cycles}
				columns={columns}
				idField='id'
				onRowClick={handleRowClick}
				showPagination={true}
				searchValue={searchQuery.search}
				onSearchChange={setSearchQuery}
				actionButtons={
					actions.includes('createCycle')
						? [
								{
									children: 'Create cycle',
									icon: AddIcon,
									iconPosition: 'left',
									onClick: () => navigate(`/organizations/${organizationId}/cycles/create/general`),
									variant: 'primary' as const,
								},
							]
						: []
				}
				emptyState={getEmptyState}
			/>

			<Modal
				data-testid='cycles-duplicate-modal'
				isOpen={isDuplicateModalOpen}
				onClose={() => {
					setIsDuplicateModalOpen(false)
					setSelectedCycle(null)
					setDuplicationOptions()
				}}
				title='Duplicate Cycle'
				buttons={[
					{
						variant: 'secondary',
						onClick: () => {
							setIsDuplicateModalOpen(false)
							setSelectedCycle(null)
							setDuplicationOptions()
						},
						children: 'Cancel',
					},
					{
						variant: 'primary',
						onClick: handleDuplicate,
						children: 'Duplicate',
					},
				]}>
				<div className='space-y-4'>
					<Text variant='small'>
						This will create a copy of the cycle <strong>"{selectedCycle?.name}"</strong> with all its configuration.
					</Text>

					<div className='bg-gray-50 p-4 rounded-md space-y-3'>
						<div className='flex items-start gap-2'>
							<div className='flex-1'>
								<Text variant='small' weight='semibold'>
									Copy admins, viewers and/or participants
								</Text>
								<Text variant='xsmall' className='text-gray-600 mt-1'>
									Include the current cycle admins, viewers and/or participants in the duplicated cycle
								</Text>

								<div className='flex flex-row justify-around'>
									<label htmlFor='copyAdmins' className='text-center'>
										<Text variant='xsmall' className='text-gray-600 mt-1'>
											Copy Admins
										</Text>
										<Checkbox
											id='copyAdmins'
											checked={duplicationOptions.copyAdmins}
											onChange={(e) => setDuplicationOptions({ copyAdmins: e.target.checked })}
											className='mt-0.5'
										/>
									</label>
									<label htmlFor='copyViewers' className='text-center'>
										<Text variant='xsmall' className='text-gray-600 mt-1'>
											Copy Viewers
										</Text>
										<Checkbox
											id='copyViewers'
											checked={duplicationOptions.copyViewers}
											onChange={(e) => setDuplicationOptions({ copyViewers: e.target.checked })}
											className='mt-0.5'
										/>
									</label>
									<label htmlFor='copyParticipants' className='text-center'>
										<Text variant='xsmall' className='text-gray-600 mt-1'>
											Copy Participants
										</Text>
										<Checkbox
											id='copyParticipants'
											checked={duplicationOptions.copyParticipants}
											onChange={(e) => setDuplicationOptions({ copyParticipants: e.target.checked })}
											className='mt-0.5'
										/>
									</label>
								</div>
							</div>
						</div>
					</div>

					<div className='bg-gray-50 p-4 rounded-md space-y-3'>
						<div className='flex items-start gap-2'>
							<div className='flex-1'>
								<Text variant='small' weight='semibold'>
									Copy cycle configurations
								</Text>
								<Text variant='xsmall' className='text-gray-600 mt-1'>
									Include cycle steps, role mappings, and reviewer groups in the duplicated cycle
								</Text>

								<div className='flex flex-col gap-2 mt-2'>
									<label htmlFor='copyCycleSteps' className='flex items-center gap-2'>
										<Checkbox
											id='copyCycleSteps'
											checked={duplicationOptions.copyCycleSteps}
											onChange={(e) => setDuplicationOptions({ copyCycleSteps: e.target.checked })}
										/>
										<Text variant='xsmall'>Copy Cycle Step Configurations</Text>
									</label>

									<label htmlFor='copyCycleRoleMappings' className='flex items-center gap-2'>
										<Checkbox
											id='copyCycleRoleMappings'
											checked={duplicationOptions.copyCycleRoleMappings}
											onChange={(e) => setDuplicationOptions({ copyCycleRoleMappings: e.target.checked })}
										/>
										<Text variant='xsmall'>Copy Role Mappings</Text>
									</label>

									<label htmlFor='copyCycleReviewerGroups' className='flex items-center gap-2'>
										<Checkbox
											id='copyCycleReviewerGroups'
											checked={duplicationOptions.copyCycleReviewerGroups}
											onChange={(e) => setDuplicationOptions({ copyCycleReviewerGroups: e.target.checked })}
										/>
										<Text variant='xsmall'>Copy Reviewer Groups</Text>
									</label>

									<label htmlFor='copyAsTemplate' className='flex items-center gap-2'>
										<Checkbox
											id='copyAsTemplate'
											checked={duplicationOptions.copyAsTemplate}
											onChange={(e) => setDuplicationOptions({ copyAsTemplate: e.target.checked })}
										/>
										<Text variant='xsmall'>Create Duplicate as Template</Text>
									</label>
								</div>
							</div>
						</div>
					</div>

					<div className='bg-blue-50 p-3 rounded-md'>
						<Text variant='xsmall' className='text-blue-800'>
							<strong>Note:</strong> The duplicated cycle will be created as a draft. You'll need to:
						</Text>
						<ul className='mt-2 ml-4 space-y-1 list-disc list-inside'>
							<li>
								<Text variant='xsmall' className='text-blue-700 inline'>
									Update the hierarchy file for new participants
								</Text>
							</li>
							<li>
								<Text variant='xsmall' className='text-blue-700 inline'>
									Review and adjust cycle settings if needed
								</Text>
							</li>
							<li>
								<Text variant='xsmall' className='text-blue-700 inline'>
									Build the cycle when ready
								</Text>
							</li>
						</ul>
					</div>
				</div>
			</Modal>

			<Modal
				data-testid='cycles-delete-modal'
				isOpen={isDeleteModalOpen}
				onClose={() => {
					setIsDeleteModalOpen(false)
					setSelectedCycle(null)
				}}
				title='Delete Cycle'
				buttons={[
					{
						variant: 'secondary',
						onClick: () => {
							setIsDeleteModalOpen(false)
							setSelectedCycle(null)
						},
						children: 'Cancel',
					},
					{
						variant: 'destructive',
						onClick: handleDelete,
						children: 'Delete',
					},
				]}>
				<div className='space-y-4'>
					<Text variant='small'>
						Are you sure you want to delete the cycle <strong>"{selectedCycle?.name}"</strong>?
					</Text>

					<div className='bg-red-50 p-3 rounded-md'>
						<Text variant='xsmall' className='text-red-800'>
							<strong>Warning:</strong> This action cannot be undone. Deleting this cycle will:
						</Text>
						<ul className='mt-2 ml-4 space-y-1 list-disc list-inside'>
							<li>
								<Text variant='xsmall' className='text-red-700 inline'>
									Permanently delete all cycle data and configurations
								</Text>
							</li>
							<li>
								<Text variant='xsmall' className='text-red-700 inline'>
									Remove all participants, reviewers, and assignments
								</Text>
							</li>
							<li>
								<Text variant='xsmall' className='text-red-700 inline'>
									Delete all evaluation data and responses
								</Text>
							</li>
						</ul>
					</div>
				</div>
			</Modal>
		</main>
	)
}
